import { useEffect, useRef, useState } from "react";
import {
  STAGE_COLORS,
  isLoud,
  urgencyOf,
  type CardGeometry,
  type Stage,
  type Task,
} from "@board/shared";
import { forgetClick, isSecondClick, noteOpened } from "./doubleClick";
import { tiltOf } from "./handDrawn";
import { dragThreshold, passedDragThreshold } from "./pointerGesture";
import { useBoard } from "./store";

/**
 * A sticky note. Position comes entirely from layoutZone — nothing about where
 * a note sits is stored, so two people writing at once can never disagree about
 * coordinates.
 *
 * Pressing a note is three gestures wearing one coat: a press that goes nowhere
 * opens it, a press that moves drags it to another section, and two presses in
 * quick succession open it inside its client's own board. DRAG_THRESHOLD is what
 * tells the first two apart, and it is in screen pixels rather than board units
 * so the gesture feels the same at every zoom level.
 *
 * The double is not decided here. The first click opens the workspace, which
 * covers the board, so the second click lands on the sheet rather than on this
 * note — see doubleClick.ts. The note only records that it was opened, and the
 * sheet asks. The alternative was to hold every click for a quarter of a second
 * to see whether a second was coming, which taxes the commonest action in the
 * app to pay for the rarest.
 */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

/** "9 Sep" — the year only when it isn't this one. */
function shortDate(iso: string, now = new Date()): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  const month = date.toLocaleString("en-GB", { month: "short", timeZone: "UTC" });
  return y === now.getFullYear() ? `${d} ${month}` : `${d} ${month} ${y}`;
}

export function Sticky({
  task,
  at,
  scale,
  onDragOver,
  onDrop,
  onExpand,
  linked = false,
  onRemoveLinked,
}: {
  task: Task;
  at: CardGeometry;
  /** Board scale, so a screen-space drag converts to board units. */
  scale: number;
  /** Called as the note moves, so the zone can light up the band underneath. */
  onDragOver: (clientX: number, clientY: number) => void;
  /** Called on release. Null means it was let go over nothing. */
  onDrop: (taskId: string, clientX: number, clientY: number) => void;
  /** Double-click: open this note inside its own client's board. */
  onExpand: (taskId: string) => void;
  /** This is an additional worklist appearance of the canonical task. */
  linked?: boolean;
  /** Removes only this worklist appearance; the canonical task remains. */
  onRemoveLinked?: () => Promise<void>;
}) {
  const users = useBoard((s) => s.users);
  const me = useBoard((s) => s.me);
  const tool = useBoard((s) => s.tool);
  const openTask = useBoard((s) => s.openTask);
  const isAdmin = useBoard((s) => s.isAdmin());
  const deleteTask = useBoard((s) => s.deleteTask);
  const [refused, setRefused] = useState(false);

  const press = useRef<{
    pointer: number;
    x: number;
    y: number;
    moved: boolean;
    threshold: number;
  } | null>(null);
  const [offset, setOffset] = useState<{ x: number; y: number } | null>(null);

  const urgency = urgencyOf(task);
  const assignee = users.find((u) => u.id === task.assignee_id) ?? null;
  // The database is the boundary; this only decides whether to offer the bin.
  const canDelete = linked || isAdmin || task.created_by === me?.id;
  // A note being placed with the pad in hand must not also be draggable.
  const grabbable = tool === "select";

  function onPointerDown(event: React.PointerEvent) {
    if (event.button !== 0 || !grabbable) return;
    // The band underneath must not treat this as a place, and the surface must
    // not treat it as a pan.
    event.stopPropagation();
    event.preventDefault();
    press.current = {
      pointer: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moved: false,
      threshold: dragThreshold(event.pointerType),
    };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: React.PointerEvent) {
    const active = press.current;
    if (!active || active.pointer !== event.pointerId) return;
    const dx = event.clientX - active.x;
    const dy = event.clientY - active.y;
    if (!active.moved && !passedDragThreshold(active.x, active.y, event.clientX, event.clientY, active.threshold)) return;
    active.moved = true;
    setOffset({ x: dx / scale, y: dy / scale });
    onDragOver(event.clientX, event.clientY);
  }

  function onPointerUp(event: React.PointerEvent) {
    const active = press.current;
    if (!active || active.pointer !== event.pointerId) return;
    press.current = null;
    setOffset(null);
    if (active.moved) {
      onDrop(task.id, event.clientX, event.clientY);
      // A drag is not half of a double-click. Forgetting this makes a drag
      // followed by a click open the client's board unasked.
      forgetClick();
      return;
    }

    // The note is only reachable for a second click when nothing is covering
    // it — an already-open workspace for a *different* task, say. Checked here
    // as well as on the sheet so the gesture works either way.
    if (isSecondClick(task.id, event.clientX, event.clientY)) {
      onExpand(task.id);
      return;
    }

    noteOpened(task.id, event.clientX, event.clientY);
    openTask(task.id);
  }

  function onPointerCancel(event: React.PointerEvent) {
    if (press.current?.pointer !== event.pointerId) return;
    press.current = null;
    setOffset(null);
    onDragOver(Number.NaN, Number.NaN);
    forgetClick();
  }

  const dragging = offset !== null;

  return (
    <article
      className={
        `sticky sticky--${urgency}` +
        (isLoud(task.priority) ? ` sticky--loud sticky--${task.priority}` : "") +
        (dragging ? " sticky--dragging" : "") +
        (refused ? " sticky--refused" : "") +
        (grabbable ? " sticky--grabbable" : "")
      }
      style={{
        left: at.x,
        top: at.y,
        width: at.width,
        height: at.height,
        background: STAGE_COLORS[task.stage],
        // Composed with the hover lift in CSS, the way the tray items are.
        ["--tilt" as string]: `${tiltOf(task.id)}deg`,
        ...(offset
          ? { transform: `translate(${offset.x}px, ${offset.y}px) rotate(-2deg) scale(1.04)` }
          : null),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      role="button"
      tabIndex={grabbable ? 0 : -1}
      aria-label={`Open ${task.title}`}
      data-task-id={task.id}
      data-linked={linked ? "true" : "false"}
      onKeyDown={(event) => {
        if (grabbable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openTask(task.id);
        }
      }}
    >
      {linked ? <span className="sticky__linked" title="Linked task" aria-label="Linked task">↗</span> : null}
      <p className="sticky__title">{task.title}</p>

      <footer className="sticky__foot">
        {assignee ? (
          <span className="sticky__who" title={assignee.name}>
            {initialsOf(assignee.name)}
          </span>
        ) : (
          <span />
        )}
        {task.due_date ? <span className="sticky__due">{shortDate(task.due_date)}</span> : null}
      </footer>

      {canDelete ? (
        <button
          className="sticky__bin"
          aria-label={linked ? `Remove linked copy of ${task.title}` : `Remove ${task.title}`}
          // Kept out of the press gesture entirely: binning a note is not a
          // very short drag.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            (linked && onRemoveLinked ? onRemoveLinked() : deleteTask(task.id)).catch(() => {
              setRefused(true);
              window.setTimeout(() => setRefused(false), 2400);
            });
          }}
        >
          ×
        </button>
      ) : null}
    </article>
  );
}

/** One compact target standing in for two or more completed notes. */
export function CompletedStack({
  task,
  at,
  count,
  onExpand,
}: {
  task: Task;
  at: CardGeometry;
  count: number;
  onExpand: () => void;
}) {
  return (
    <button
      type="button"
      className="completed-stack"
      style={{
        left: at.x,
        top: at.y,
        width: at.width,
        height: at.height,
        background: STAGE_COLORS.completed,
        ["--tilt" as string]: `${tiltOf(task.id)}deg`,
      }}
      aria-label={`Show ${count} completed notes`}
      aria-expanded="false"
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        onExpand();
      }}
    >
      <span className="completed-stack__title">{task.title}</span>
      <span className="completed-stack__count">{count} completed</span>
    </button>
  );
}

/**
 * A blank note, mid-write. It exists only in the placing zone's local state —
 * nothing is sent until there is a title, so an abandoned note leaves no row.
 */
export function StickyDraft({
  stage,
  at,
  onCommit,
  onCancel,
}: {
  stage: Stage;
  at: { x: number; y: number; width: number; height: number };
  onCommit: (title: string) => void;
  onCancel: () => void;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => field.current?.focus(), []);

  return (
    <article
      className="sticky sticky--draft"
      style={{
        left: at.x,
        top: at.y,
        width: at.width,
        height: at.height,
        background: STAGE_COLORS[stage],
      }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <textarea
        ref={field}
        className="sticky__field"
        placeholder="What needs doing?"
        rows={3}
        onKeyDown={(event) => {
          // Enter commits; Shift+Enter is a line break, since a task title can
          // reasonably wrap. Esc abandons the note.
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            const title = event.currentTarget.value.trim();
            if (title) onCommit(title);
            else onCancel();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        onBlur={(event) => {
          // Clicking away is a commit if anything was written, otherwise a
          // discard — losing three words to a stray click is not acceptable.
          const title = event.currentTarget.value.trim();
          if (title) onCommit(title);
          else onCancel();
        }}
      />
    </article>
  );
}
